import { type FormEvent, useState } from 'react';
import { api } from '../lib/api';
import { Card, Empty, ErrorNotice } from '../components/ui';

/** Wireframe B7 — coupon generator. */
export function Coupons() {
  const [count, setCount] = useState('10');
  const [permittedDevices, setPermittedDevices] = useState('2');
  const [validUntil, setValidUntil] = useState('');
  const [codePrefix, setCodePrefix] = useState('');
  const [generated, setGenerated] = useState<string[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function generate(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ coupons: string[]; devicesNotified: number }>('/coupons/generate', {
        count: Number(count),
        permittedDevices: Number(permittedDevices),
        validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
        codePrefix: codePrefix || undefined,
      });
      setGenerated(result.coupons);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  function copyAll() {
    if (generated) void navigator.clipboard?.writeText(generated.join('\n'));
  }

  return (
    <>
      <div className="page-head">
        <h1>Guest coupons</h1>
        <p>Generate codes for guests to redeem when they connect.</p>
      </div>

      <ErrorNotice error={error} />

      <Card title="Generate coupons">
        <form className="card-body" onSubmit={generate}>
          <div className="row">
            <div className="field">
              <label htmlFor="count">How many</label>
              <input id="count" type="number" min={1} max={500} value={count} onChange={(e) => setCount(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="devices">Devices allowed per coupon</label>
              <input
                id="devices"
                type="number"
                min={1}
                max={20}
                value={permittedDevices}
                onChange={(e) => setPermittedDevices(e.target.value)}
              />
            </div>
          </div>
          <div className="row">
            <div className="field">
              <label htmlFor="until">Valid until</label>
              <input id="until" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
              <div className="hint">Leave blank for no expiry.</div>
            </div>
            <div className="field">
              <label htmlFor="prefix">Code prefix</label>
              <input
                id="prefix"
                maxLength={6}
                placeholder="MARINA"
                value={codePrefix}
                onChange={(e) => setCodePrefix(e.target.value.toUpperCase())}
              />
            </div>
          </div>
          <button type="submit" disabled={busy}>
            {busy ? 'Generating…' : 'Generate coupons'}
          </button>
        </form>
      </Card>

      {generated ? (
        <Card
          title={`${generated.length} coupons generated`}
          action={
            <button className="secondary small" onClick={copyAll}>
              Copy all
            </button>
          }
        >
          <div className="card-body">
            <div className="notice info">
              Save or print these now — for guests&rsquo; security, codes are shown only once.
            </div>
            {generated.map((code) => (
              <div className="coupon-code" key={code}>
                {code}
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card>
          <Empty title="No coupons generated yet">
            <p className="muted">Generated codes appear here, ready to print or share.</p>
          </Empty>
        </Card>
      )}
    </>
  );
}
